import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import ProductCard from "@/components/storefront/ProductCard";

const Countdown = ({ to, label }) => {
  const [left, setLeft] = useState(() => new Date(to).getTime() - Date.now());
  useEffect(() => {
    const t = setInterval(() => setLeft(new Date(to).getTime() - Date.now()), 1000);
    return () => clearInterval(t);
  }, [to]);
  if (left <= 0) return null;
  const d = Math.floor(left / 86400000), h = Math.floor(left / 3600000) % 24,
    m = Math.floor(left / 60000) % 60, s = Math.floor(left / 1000) % 60;
  const Unit = ({ v, u }) => (
    <span className="flex flex-col items-center">
      <span className="font-serif text-4xl md:text-5xl leading-none">{String(v).padStart(2, "0")}</span>
      <span className="text-[0.58rem] tracking-[0.18em] uppercase text-cream/60 mt-1">{u}</span>
    </span>
  );
  return (
    <div data-testid="drop-countdown" className="mt-6">
      <p className="overline text-gold mb-3">{label}</p>
      <div className="flex gap-6 md:gap-8 text-cream">
        <Unit v={d} u="days" /><Unit v={h} u="hrs" /><Unit v={m} u="min" /><Unit v={s} u="sec" />
      </div>
    </div>
  );
};

const CollectionPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/collections/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) return <div className="min-h-[60vh] flex items-center justify-center font-serif text-3xl">She's not here.</div>;
  if (!data) return <div className="min-h-[60vh]" />;
  const c = data.collection;
  const isEditorial = c.type === "drop" || c.type === "editorial";
  const now = Date.now();
  const ea = c.early_access_at ? new Date(c.early_access_at).getTime() : 0;
  const gl = c.go_live_at ? new Date(c.go_live_at).getTime() : 0;
  let cdTo = null, cdLabel = "";
  if (ea && ea > now) { cdTo = c.early_access_at; cdLabel = "Early access opens in"; }
  else if (gl && gl > now) { cdTo = c.go_live_at; cdLabel = "Launches in"; }

  return (
    <div>
      <section className="relative h-[60vh] min-h-[420px] -mt-16 md:-mt-20 flex items-end overflow-hidden bg-espresso">
        {/* Premium frame — blurred wash of the same image fills the edges; full image shown, never a cropped box. */}
        <div aria-hidden className="absolute inset-0 bg-center bg-cover scale-125 blur-2xl opacity-55" style={{ backgroundImage: `url(${mediaUrl(c.hero_desktop)})` }} />
        <img src={mediaUrl(c.hero_desktop)} alt={c.heading} className="absolute inset-0 w-full h-full object-contain" />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 to-espresso/10" />
        <div className="relative max-w-[1600px] mx-auto w-full px-6 md:px-10 pb-12 md:pb-16 text-cream">
          {c.editorial_opening && <p className="overline text-gold mb-3">{c.editorial_opening}</p>}
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.9]">{c.heading || c.name}</h1>
          {c.subheading && <p className="font-serif text-2xl md:text-3xl italic mt-3 text-cream/90">{c.subheading}</p>}
          {cdTo && <Countdown to={cdTo} label={cdLabel} />}
        </div>
      </section>

      {isEditorial && c.story && (
        <section className="py-16 md:py-24 px-6 max-w-3xl mx-auto text-center">
          <p className="font-serif text-2xl md:text-3xl leading-relaxed text-espresso/80">{c.story}</p>
        </section>
      )}

      {c.colour_story?.length > 0 && (
        <section className="pb-4 px-6 max-w-[1600px] mx-auto">
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {c.colour_story.map((cs) => (
              <div key={cs.name} className="flex flex-col items-center gap-2">
                <span className="w-12 h-12 md:w-14 md:h-14 rounded-full border border-espresso/10" style={{ background: cs.swatch }} />
                <span className="text-[0.64rem] tracking-[0.14em] uppercase text-espresso/60">{cs.name}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="py-16 md:py-20 px-6 md:px-10 max-w-[1600px] mx-auto">
        {data.products.length === 0 ? (
          <p className="text-center font-serif text-3xl py-20 text-espresso/60">This chapter is coming soon.</p>
        ) : (
          <>
            <p className="overline text-espresso/50 mb-12">{data.products.length} piece{data.products.length !== 1 ? "s" : ""}</p>
            <div className="grid grid-cols-2 md:grid-cols-12 gap-x-5 gap-y-14 md:gap-x-12 md:gap-y-10">
              {data.products.map((p, i) => (
                <div key={p.id} className={`md:col-span-6 ${i % 2 ? "md:mt-32" : ""}`}>
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default CollectionPage;
